import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useProcesses, useSetProcessEnabled } from '../../api/processes.js';
import { processTypeLabel } from '../../utils/constants.js';
import CreateProcessModal from '../CreateProcessModal.jsx';
import Spinner from '../Spinner.jsx';
import ErrorBanner from '../ErrorBanner.jsx';

export default function ProcessesPanel({ companyId, companyName, selectedProcessId, onSelect }) {
    const { t } = useTranslation();
    const [includeDisabled, setIncludeDisabled] = useState(true);
    const [showCreate, setShowCreate] = useState(false);

    const { data: processes, isLoading, error } = useProcesses(companyId, includeDisabled);
    const toggleEnabled = useSetProcessEnabled(companyId);

    return (
        <div className="workspace-panel">
            <div className="panel-header">
                <div className="panel-header-titles">
                    <h2>{t('processes.title')}</h2>
                    {companyId && <div className="panel-subtitle">{companyName}</div>}
                </div>
                {companyId && (
                    <button type="button" className="btn btn-sm btn-primary" onClick={() => setShowCreate(true)}>
                        {t('processes.new')}
                    </button>
                )}
            </div>

            {!companyId && <div className="panel-empty">{t('processes.selectCompanyPrompt')}</div>}

            {companyId && (
                <>
                    <div className="panel-body">
                        {isLoading && <Spinner />}
                        <ErrorBanner error={error} fallback={t('processes.loadError')} />

                        {!isLoading && processes?.length === 0 && (
                            <div className="panel-empty">{t('processes.empty')}</div>
                        )}

                        {processes?.map((process) => (
                            <div
                                key={process.id}
                                role="button"
                                tabIndex={0}
                                className={`panel-list-item ${process.id === selectedProcessId ? 'active' : ''}`}
                                onClick={() => onSelect(process.id)}
                                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSelect(process.id)}
                            >
                                <span className="item-title">
                                    {processTypeLabel(process.type, t)}
                                    <span className={`badge ${process.enabled ? 'badge-success' : 'badge-neutral'}`}>
                                        {process.enabled ? t('processes.on') : t('processes.off')}
                                    </span>
                                </span>
                                <button
                                    type="button"
                                    className="btn btn-sm item-meta"
                                    style={{ marginTop: '0.35rem' }}
                                    disabled={toggleEnabled.isPending}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleEnabled.mutate({ processId: process.id, enabled: !process.enabled });
                                    }}
                                >
                                    {process.enabled ? t('processes.disable') : t('processes.enable')}
                                </button>
                            </div>
                        ))}
                    </div>

                    <div className="panel-footer">
                        <label className="checkbox-row">
                            <input
                                type="checkbox"
                                checked={includeDisabled}
                                onChange={(e) => setIncludeDisabled(e.target.checked)}
                            />
                            {t('processes.showDisabled')}
                        </label>
                    </div>
                </>
            )}

            {showCreate && (
                <CreateProcessModal
                    companyId={companyId}
                    onClose={() => setShowCreate(false)}
                    onCreated={(data) => onSelect(data.id)}
                />
            )}
        </div>
    );
}
